import { useRef, useCallback, useEffect } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";

const VISIBLE = 5;

export function WheelPicker({ items, selected, onSelect, renderItem, testIdPrefix = "wheel" }) {
  const selectedIdx = Math.max(0, items.findIndex((it) => it.value === selected));
  const containerRef = useRef(null);
  const touchStartY = useRef(null);
  const lastScrollTime = useRef(0);

  const half = Math.floor(VISIBLE / 2);
  const startIdx = Math.max(0, Math.min(selectedIdx - half, items.length - VISIBLE));
  const visibleItems = items.slice(startIdx, startIdx + VISIBLE);

  const goUp = useCallback(() => {
    for (let i = selectedIdx - 1; i >= 0; i--) {
      if (!items[i].disabled) { onSelect(items[i].value); return; }
    }
  }, [selectedIdx, items, onSelect]);

  const goDown = useCallback(() => {
    for (let i = selectedIdx + 1; i < items.length; i++) {
      if (!items[i].disabled) { onSelect(items[i].value); return; }
    }
  }, [selectedIdx, items, onSelect]);

  // Use native event listener for wheel to bypass React passive default
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handler = (e) => {
      e.preventDefault();
      const now = Date.now();
      if (now - lastScrollTime.current < 120) return; // throttle
      lastScrollTime.current = now;
      if (e.deltaY > 0) goDown();
      else if (e.deltaY < 0) goUp();
    };
    el.addEventListener("wheel", handler, { passive: false });
    return () => el.removeEventListener("wheel", handler);
  }, [goUp, goDown]);

  const handleTouchStart = useCallback((e) => {
    touchStartY.current = e.touches[0].clientY;
  }, []);

  const handleTouchEnd = useCallback((e) => {
    if (touchStartY.current === null) return;
    const delta = touchStartY.current - e.changedTouches[0].clientY;
    touchStartY.current = null;
    if (Math.abs(delta) < 15) return;
    if (delta > 0) goDown();
    else goUp();
  }, [goUp, goDown]);

  const canUp = items.slice(0, selectedIdx).some(i => !i.disabled);
  const canDown = items.slice(selectedIdx + 1).some(i => !i.disabled);

  return (
    <div
      className="select-none touch-none"
      data-testid={`${testIdPrefix}-container`}
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <button
        onClick={goUp}
        disabled={!canUp}
        className={`w-full flex justify-center py-3 rounded-t-2xl border-2 border-b-0 border-[#09090B] transition-all
          ${canUp ? "bg-[#FAFAFA] hover:bg-[#FFE600] active:bg-[#FFE600] cursor-pointer" : "bg-[#FAFAFA] opacity-30 cursor-not-allowed"}`}
        data-testid={`${testIdPrefix}-up`}
      >
        <ChevronUp className="h-6 w-6 text-[#09090B]" strokeWidth={2.5} />
      </button>

      <div className="border-2 border-[#09090B] bg-white overflow-hidden">
        {visibleItems.map((item) => {
          const isSel = item.value === selected;
          const globalIdx = items.indexOf(item);
          const distFromCenter = Math.abs(globalIdx - selectedIdx);
          const opacity = distFromCenter === 0 ? "opacity-100" : distFromCenter === 1 ? "opacity-60" : "opacity-30";

          return (
            <div
              key={item.value}
              onClick={() => { if (!item.disabled) onSelect(item.value); }}
              className={`flex items-center justify-center h-[56px] transition-all duration-150 cursor-pointer relative
                ${item.disabled ? "cursor-not-allowed" : ""}
                ${isSel ? "bg-[#FFE600]/30 border-y border-[#FFE600]" : "hover:bg-[#FAFAFA]"}
                ${!isSel ? opacity : ""}
              `}
              data-testid={`${testIdPrefix}-item-${item.value}`}
            >
              {renderItem ? renderItem(item, isSel) : (
                <span className={`text-base transition-all
                  ${item.disabled ? "text-[#D4D4D8] line-through" : ""}
                  ${isSel && !item.disabled ? "font-black text-lg text-[#09090B]" : "text-[#71717A]"}
                `}>
                  {item.label}
                </span>
              )}
            </div>
          );
        })}
      </div>

      <button
        onClick={goDown}
        disabled={!canDown}
        className={`w-full flex justify-center py-3 rounded-b-2xl border-2 border-t-0 border-[#09090B] transition-all
          ${canDown ? "bg-[#FAFAFA] hover:bg-[#FFE600] active:bg-[#FFE600] cursor-pointer" : "bg-[#FAFAFA] opacity-30 cursor-not-allowed"}`}
        data-testid={`${testIdPrefix}-down`}
      >
        <ChevronDown className="h-6 w-6 text-[#09090B]" strokeWidth={2.5} />
      </button>
    </div>
  );
}
